import {
  bumpPerspectivesVersion,
  setPerspectiveId,
  usePerspectiveId,
  usePerspectivesVersion,
} from "@app/lib/perspective";
import { environment } from "@app/relay/relay-environment";
import { useRouter } from "@tanstack/react-router";
import { Suspense } from "react";
import { commitMutation, graphql, useLazyLoadQuery } from "react-relay";

import type { perspectiveSwitcher_Query } from "@app/queries/perspectiveSwitcher_Query.graphql";
import type { perspectiveSwitcher_CreatePerspectiveMutation } from "@app/queries/perspectiveSwitcher_CreatePerspectiveMutation.graphql";

const perspectivesQuery = graphql`
  query perspectiveSwitcher_Query {
    perspectives {
      id
      name
    }
  }
`;

const createPerspectiveMutation = graphql`
  mutation perspectiveSwitcher_CreatePerspectiveMutation(
    $input: CreatePerspectiveInputObject!
  ) {
    createPerspective(input: $input) {
      perspective {
        id
        name
      }
      errors {
        message
      }
    }
  }
`;

const NEW_VALUE = "__new__";

export default function PerspectiveSwitcher() {
  return (
    <Suspense fallback={null}>
      <Switcher />
    </Suspense>
  );
}

function Switcher() {
  const version = usePerspectivesVersion();
  const data = useLazyLoadQuery<perspectiveSwitcher_Query>(
    perspectivesQuery,
    {},
    {
      fetchKey: version,
      fetchPolicy: version === 0 ? "store-or-network" : "network-only",
    }
  );
  const router = useRouter();
  const selectedId = usePerspectiveId();

  const perspectives = data.perspectives;
  const defaultId = perspectives[0]?.id;
  // A stale stored id (deleted perspective) falls back to the default lens
  const currentId = perspectives.some((p) => p.id === selectedId)
    ? selectedId
    : defaultId;

  const switchTo = (id: string) => {
    setPerspectiveId(id === defaultId ? null : id);
    router.invalidate();
  };

  const createPerspective = () => {
    const name = window.prompt("Name the new perspective:");
    if (!name?.trim()) return;

    commitMutation<perspectiveSwitcher_CreatePerspectiveMutation>(environment, {
      mutation: createPerspectiveMutation,
      variables: { input: { name: name.trim() } },
      onCompleted: (response) => {
        const created = response.createPerspective?.perspective;
        if (!created) return;
        setPerspectiveId(created.id);
        bumpPerspectivesVersion();
        router.invalidate();
      },
    });
  };

  // The lens only matters once there is something to look through
  if (perspectives.length === 0) return null;

  return (
    <select
      value={currentId ?? ""}
      onChange={(event) => {
        if (event.target.value === NEW_VALUE) {
          createPerspective();
        } else {
          switchTo(event.target.value);
        }
      }}
      title="Perspective — the categorization lens applied to every category-based view"
      className="ml-2 text-sm border border-gray-300 rounded px-2 py-1 bg-white text-gray-700 hover:border-gray-400 max-w-44 truncate"
    >
      {perspectives.map((perspective) => (
        <option key={perspective.id} value={perspective.id}>
          {perspective.name}
        </option>
      ))}
      <option value={NEW_VALUE}>+ New perspective…</option>
    </select>
  );
}
